/**
 * THE PAYMENT TYPES CARD — the one block the terminal's Payment Types tab and
 * the order's device card both draw, so the two cannot drift. Layout is the
 * approved mockup (payment-types-three-layer.html, tabs 2 and 3):
 *
 *   Cash    → Require customer signature · Tips
 *   Credit  → Require customer signature · Charge a surcharge · Tips
 *   Debit   → Require customer signature · Tips (linked with Credit)
 *   Split payment · EBT · Card — external terminal
 *
 * In Super Admin the main switches ASSIGN; a tender the merchant switched off
 * says so. Tips rows are the LOCATION grant. No vendor name appears on any
 * row — "Credit", "Debit", "Card"; the reader line names only its transport.
 *
 * Purely controlled: it edits a draft and never saves. The page's one save bar
 * does that, sending only what changed.
 */
import React from 'react';
import { isTapToPayDevice } from '../RubyTillPayRegisterFields';
import { Banknote, CreditCard, Split, Landmark, PenLine, Percent, DollarSign, MinusSquare } from 'lucide-react';
import { readSignatureRequired } from '../signatureRequired';
import {
  RegisterState, GrantState, DEVICE_CREDIT_TYPES, EXTERNAL_CARD_ALIASES, SIG_DEBIT,
  creditTenderOf, tenderLive, merchantTurnedOff, effectiveSurcharge, effectiveDebit,
  isSurchargeApproved, activeAgreementOf, assignTender, unassignTender, toggleSig, setSurchargeApply,
  TRANSPORT_FOR_CLASS,
} from './model';
import { Card, Row, SubRow, Switch, Pill, ScopeBadge, Warn, Bad, TextAction } from './ui';

export interface ReaderInfo {
  /** "Card reader on this till · USB · serial ••4471" — never a vendor. */
  line: string;
  /** Known disconnected. */
  down?: boolean;
}

export interface RegisterPaymentTypesProps {
  draft: RegisterState;
  onChange: (next: RegisterState) => void;
  grants: GrantState;
  onGrantsChange: (next: GrantState) => void;
  /** The location's gateway row — null while unread (rows that need it lock). */
  gateway: any | null;
  locationName?: string;
  reader?: ReaderInfo | null;
  /** Super Admin turning Credit on — the caller picks the reader and calls back. */
  onAssignCredit: () => void;
  /** Why Credit cannot be assigned here right now (no coupled / registered reader). */
  creditBlocked?: string | null;
  /** Where surcharge approval is made, for the locked row's text. */
  approvalWhere?: string;
  disabled?: boolean;
  /** The location card (tip choices, rate, minimum). Default on. */
  showLocationCard?: boolean;
  /**
   * The bound reader's own settings, drawn right under the Credit row while
   * Credit is assigned — today only RubyTill Pay terminals have one
   * (RubyTillPayDevicePanel). It saves itself; the page's save bar never does.
   */
  creditDevicePanel?: React.ReactNode;
  /**
   * SUNMI card setup (sunmi-card-setup.md) — ONLY for a register whose catalog
   * model has a `cardSetupPolicy` AND which has a stored `cardSetup`. Absent,
   * every row below renders exactly as before.
   */
  cardSetup?: CardSetupView | null;
  /** An editable location card (Super Admin terminal tab) in place of the read-only one. */
  locationEditor?: React.ReactNode;
}

/** What the SUNMI card setup adds to this card. Stored fields are unchanged. */
export interface CardSetupView {
  /** Replaces the Credit row's single reader line ("Primary · … / Backup · …"). */
  readersLine: React.ReactNode;
  /** Replaces the Credit row's assign switch — readers are set on the Terminal tab. */
  creditRight?: React.ReactNode;
  /** Capability lock: debit (and so surcharge) needs a reader that takes PIN. */
  debit: { possible: boolean; readerName: string | null; reason: string | null };
  /** "Tips location" sub-row under Credit. */
  tipLocationRow?: React.ReactNode;
}

/** "Card reader on this till · USB · serial ••4471" — transport only, never a vendor. */
export function readerInfoFor(device: any | null): ReaderInfo | null {
  if (!device) return null;
  /* RubyTill Pay is our own product, so the row may name it — and the device
     name, which is how the shop tells two counters' terminals apart. */
  const isRtpay = String(device.deviceClass || '') === 'dejavoo_rubytill_pay';
  const tapToPay = isRtpay && isTapToPayDevice(device);
  const parts = [isRtpay ? `${device.deviceName || 'Card terminal'} — ${tapToPay ? 'Android Tap to Pay' : 'RubyTill Pay'}` : 'Card reader on this till'];
  const transport = TRANSPORT_FOR_CLASS[String(device.deviceClass || '')];
  if (transport && !isRtpay) parts.push(transport);
  const serial = String(device.serialNumber || '').trim();
  if (serial) parts.push(`serial ••${serial.slice(-4)}`);
  const status = String(device.connectionStatus || device.status || '').toLowerCase();
  const down = device.connected === false || status === 'disconnected' || status === 'offline';
  return { line: parts.join(' · '), down };
}

const TIPS_LOC = <ScopeBadge kind="loc" small>Every till at location</ScopeBadge>;

export const RegisterPaymentTypes: React.FC<RegisterPaymentTypesProps> = ({
  draft, onChange, grants, onGrantsChange, gateway, locationName, reader, onAssignCredit,
  creditBlocked, approvalWhere = 'approve it in the Admin section above', disabled, showLocationCard = true,
  creditDevicePanel, cardSetup, locationEditor,
}) => {
  const ov = draft.overrideActive;
  const loc = locationName || 'this location';
  const gwRead = gateway !== null && gateway !== undefined;
  const approved = isSurchargeApproved(gateway, grants);
  const agreement = activeAgreementOf(gateway);
  const tipCfg = gateway?.tenantTipConfig || null;
  const merchantCardTips = !!tipCfg?.enabled;
  const merchantCashTips = typeof tipCfg?.cashEnabled === 'boolean' ? tipCfg.cashEnabled : !!tipCfg?.enabled;

  const credit = creditTenderOf(draft);
  const creditAs = !!credit;
  const creditOn = creditAs && tenderLive(draft, DEVICE_CREDIT_TYPES);
  /* SUNMI only: no reader here takes PIN ⇒ debit is off and surcharge (which
     needs debit) is locked off. Never true without a card setup. */
  const pinLock = !!cardSetup && !cardSetup.debit.possible;
  const surOn = creditOn && !pinLock && effectiveSurcharge(draft, gateway, grants);
  const debitOn = creditOn && !pinLock && effectiveDebit(draft, gateway, grants);

  const assignSwitch = (ids: readonly string[], label: string, onToggle: (on: boolean) => void, blocked?: string | null) => {
    const as = draft.assigned.some((m) => ids.includes(m));
    return (
      <>
        {!as && <Pill>Not assigned</Pill>}
        {ov && as && <Pill tone="lock">Override on</Pill>}
        <Switch on={as} label={`Assign ${label}`} disabled={disabled || (!as && !!blocked)} onChange={onToggle} />
      </>
    );
  };
  const offPill = (ids: readonly string[]) => merchantTurnedOff(draft, ids) ? <Pill>Merchant turned off</Pill> : null;

  const sigRow = (key: string, what: string, label: string, testId: string) => (
    <SubRow
      testId={testId}
      icon={<PenLine size={16} />}
      title="Require customer signature"
      desc={`Asked on the customer display after an approved ${what}`}
      right={(
        <Switch
          on={readSignatureRequired(draft.signatureRequired, key)}
          label={label}
          disabled={disabled}
          onChange={() => onChange(toggleSig(draft, key))}
        />
      )}
    />
  );

  const tipsRow = (kind: 'cash' | 'card', linked?: boolean) => {
    const grant = kind === 'cash' ? grants.cashTipsEnabled : grants.tipsEnabled;
    const merchantOn = kind === 'cash' ? merchantCashTips : merchantCardTips;
    let desc: React.ReactNode;
    if (!gwRead) desc = <Warn>Location settings could not be read — tips are left as they are.</Warn>;
    else if (!grant) desc = <Warn>Not allowed for this location — switching this on grants it for every till at {loc}.</Warn>;
    else if (kind === 'cash') desc = 'Tip added to what the customer hands over; change worked out from the new total. Recorded on the sale, separate from the tip drop.';
    else desc = 'Customer is prompted on the reader after insert or tap. One switch for Credit and Debit.';
    return (
      <SubRow
        testId={`pt-tips-${kind}${linked ? '-debit' : ''}`}
        icon={<DollarSign size={16} />}
        title="Tips"
        badge={<>
          {TIPS_LOC}
          {linked && <Pill title="Same switch as Credit">🔗 linked with Credit</Pill>}
        </>}
        desc={desc}
        right={<>
          {gwRead && grant && <Pill tone={merchantOn ? 'ok' : 'neutral'}>Merchant: {merchantOn ? 'on' : 'off'}</Pill>}
          <Switch
            on={grant}
            label={`${kind === 'cash' ? 'Cash' : 'Card'} tips${linked ? ' (debit)' : ''}`}
            disabled={disabled || !gwRead}
            onChange={(v) => onGrantsChange(kind === 'cash' ? { ...grants, cashTipsEnabled: v } : { ...grants, tipsEnabled: v })}
          />
        </>}
      />
    );
  };

  // ── Surcharge sub-row ────────────────────────────────────────────────────
  const surchargeRow = () => {
    const unset = draft.surchargeApply === null;
    /* This till has its own value — hand it back to the location's setting
       (saved as `surchargeApply: null`, which the server stores as unset). */
    const followLocation = (
      <TextAction
        testId="pt-surcharge-follow"
        title={`Use ${loc}'s surcharge setting instead of this till's own`}
        disabled={disabled}
        onClick={() => onChange(setSurchargeApply(draft, null))}
      >
        Follow location
      </TextAction>
    );
    const ts = gateway?.tenantSurchargeConfig || {};
    const maxRate = Number(gateway?.maxSurchargeRate ?? agreement?.maxRate ?? 0);
    const maxFlat = Number(gateway?.maxSurchargeFlat ?? agreement?.maxFlat ?? 0);
    const rate = Number(ts.rateOverride) > 0 ? Number(ts.rateOverride) : maxRate;
    const flat = Number(ts.flatOverride) > 0 ? Number(ts.flatOverride) : maxFlat;
    if (pinLock) {
      return (
        <SubRow
          testId="pt-surcharge"
          icon={<Percent size={16} />}
          title="Charge a surcharge"
          desc={<Warn>Surcharge needs debit, so it needs a PIN-capable reader — {cardSetup!.debit.reason || 'no reader on this register can take PIN'}.</Warn>}
          right={<><Pill tone="lock">Needs a PIN reader</Pill><Switch on={false} locked disabled label="Surcharge" /></>}
        />
      );
    }
    if (!gwRead || !approved) {
      return (
        <SubRow
          testId="pt-surcharge"
          icon={<Percent size={16} />}
          title="Charge a surcharge"
          desc={<>
            <Warn>{gwRead ? `Not approved for ${loc} — ${approvalWhere}.` : 'Location settings could not be read.'}</Warn>
            {draft.surchargeApply === true && gwRead && (
              <> <Warn>This till is set to surcharge, but nothing is being charged; it resumes by itself if {loc} is approved again.</Warn></>
            )}
          </>}
          right={<>
            {draft.surchargeApply !== null && gwRead && followLocation}
            <Switch on={false} locked disabled label="Surcharge" />
          </>}
        />
      );
    }
    return (
      <SubRow
        testId="pt-surcharge"
        icon={<Percent size={16} />}
        title="Charge a surcharge"
        desc={<>Adds {rate.toFixed(2)}% + ${flat.toFixed(2)} to credit only — subtotal, never tax or tip. <b>Turning this on turns Debit on.</b></>}
        right={<>
          {unset ? <Pill tone="follow">Follows location</Pill> : followLocation}
          <Switch on={surOn} label="Surcharge" disabled={disabled} onChange={(v) => onChange(setSurchargeApply(draft, v))} />
        </>}
      />
    );
  };

  // ── Debit row ────────────────────────────────────────────────────────────
  const debitRow = () => {
    let desc: React.ReactNode; let right: React.ReactNode;
    const route = cardSetup?.debit.readerName ? <>PIN debit always goes to <b>{cardSetup.debit.readerName}</b>. </> : null;
    if (!creditOn) {
      desc = 'Needs Credit on — debit runs on the same card reader';
      right = <Switch on={false} disabled locked label="Debit" />;
    } else if (pinLock) {
      desc = <Warn>{cardSetup!.debit.reason || 'No reader on this register can take PIN'} — debit is off.</Warn>;
      right = <><Pill tone="lock">Needs a PIN reader</Pill><Switch on={false} disabled locked label="Debit" /></>;
    } else if (surOn) {
      desc = <>{route}<Warn>On automatically while Credit charges a surcharge — the customer must be able to pick debit and pay no fee.</Warn></>;
      right = <><Pill tone="lock">Auto · surcharge on</Pill><Switch on disabled locked label="Debit" /></>;
    } else {
      const text = debitOn
        ? 'Customer is asked "credit or debit?" on the display. Debit is never surcharged.'
        : "Off — the customer isn't asked; every card is charged as credit. Turn off only if the reader can't take debit.";
      desc = route ? <>{route}{text}</> : text;
      right = <>
        {draft.debitEnabled === null
          ? <Pill tone="follow">Follows surcharge</Pill>
          : (
            <TextAction
              testId="pt-debit-follow"
              title="Let Debit follow this till's surcharge setting instead of its own value"
              disabled={disabled}
              onClick={() => onChange({ ...draft, debitEnabled: null })}
            >
              Follow surcharge
            </TextAction>
          )}
        <Switch on={debitOn} label="Debit" disabled={disabled} onChange={() => onChange({ ...draft, debitEnabled: !debitOn })} />
      </>;
    }
    return <Row testId="pt-debit" icon={<CreditCard size={18} />} iconOn={debitOn} title="Debit" desc={desc} right={right} />;
  };

  const simpleTender = (id: string, ids: readonly string[], label: string, icon: React.ReactNode, desc: string, sig?: boolean) => {
    const as = draft.assigned.some((m) => ids.includes(m));
    const on = tenderLive(draft, ids);
    return (
      <React.Fragment key={id}>
        <Row
          testId={`pt-${id}`}
          icon={icon}
          iconOn={on}
          title={<>{label}{offPill(ids)}</>}
          desc={as ? desc : 'Not assigned to this till'}
          right={assignSwitch(ids, label, (v) => onChange(v ? assignTender(draft, id) : unassignTender(draft, id)))}
        />
        {on && sig && sigRow(draft.assigned.find((m) => ids.includes(m)) || id, `${label} payment`, `${label} signature`, `pt-sig-${id}`)}
      </React.Fragment>
    );
  };

  // ── Credit description ───────────────────────────────────────────────────
  let creditDesc: React.ReactNode;
  if (!creditAs) creditDesc = creditBlocked ? <><span>Not assigned to this till</span> · <Warn>{creditBlocked}</Warn></> : 'Not assigned to this till';
  else if (reader?.down) creditDesc = <Bad>Card reader disconnected — the till shows Card in red</Bad>;
  else creditDesc = reader?.line || 'Card reader on this till';

  const cashAs = draft.assigned.includes('cash');
  const cashOn = tenderLive(draft, ['cash']);

  return (
    <>
      <Card
        title="Payment types"
        sub="What this till offers at checkout. Every payment type has the same rows underneath."
        badge={<ScopeBadge kind="reg">This till</ScopeBadge>}
      >
        {/* CASH */}
        <Row
          testId="pt-cash"
          icon={<Banknote size={18} />}
          iconOn={cashOn}
          title={<>Cash{offPill(['cash'])}</>}
          desc={cashAs ? 'Bills and coins' : 'Not assigned to this till'}
          right={assignSwitch(['cash'], 'Cash', (v) => onChange(v ? assignTender(draft, 'cash') : unassignTender(draft, 'cash')))}
        />
        {cashOn && <>
          {sigRow('cash', 'cash sale', 'Cash signature', 'pt-sig-cash')}
          {tipsRow('cash')}
        </>}

        {/* CREDIT */}
        <Row
          testId="pt-credit"
          icon={<CreditCard size={18} />}
          iconOn={creditOn}
          title={<>Credit{offPill(DEVICE_CREDIT_TYPES)}</>}
          desc={cardSetup ? cardSetup.readersLine : creditDesc}
          right={cardSetup?.creditRight ?? assignSwitch(DEVICE_CREDIT_TYPES, 'Credit', (v) => {
            if (v) onAssignCredit();
            else if (credit) onChange(unassignTender(draft, credit));
          }, creditBlocked)}
        />
        {creditAs && creditDevicePanel}
        {/* SUNMI: the lock reasons and tip location show even before Credit is on. */}
        {cardSetup && !creditOn && <>
          {pinLock && surchargeRow()}
          {cardSetup.tipLocationRow}
        </>}
        {creditOn && credit && <>
          {sigRow(credit, 'credit payment', 'Credit signature', 'pt-sig-credit')}
          {surchargeRow()}
          {tipsRow('card')}
          {cardSetup?.tipLocationRow}
        </>}

        {/* DEBIT */}
        {debitRow()}
        {debitOn && <>
          {sigRow(SIG_DEBIT, 'debit payment', 'Debit signature', 'pt-sig-debit')}
          {tipsRow('card', true)}
        </>}

        {/* SPLIT / EBT / EXTERNAL */}
        {simpleTender('split', ['split'], 'Split payment', <Split size={18} />, 'Pay with two or more methods — signature is asked per card leg')}
        {simpleTender('ebt', ['ebt'], 'EBT', <Landmark size={18} />, 'Electronic Benefit Transfer', true)}
        {simpleTender('credit_external', EXTERNAL_CARD_ALIASES, 'Card — external terminal', <CreditCard size={18} />, 'A standalone card machine, settled outside the till', true)}
      </Card>

      {showLocationCard && (locationEditor ?? <LocationValuesCard gateway={gateway} locationName={loc} approved={approved} />)}
    </>
  );
};

/**
 * TIP CHOICES, SURCHARGE RATE & CARD MINIMUM — the merchant's location values.
 * Read-only in Super Admin: there is no endpoint here that writes them, and
 * they are the merchant's choice.
 */
export const LocationValuesCard: React.FC<{ gateway: any | null; locationName: string; approved: boolean }> = ({ gateway, locationName, approved }) => {
  const tag = <Pill>Merchant's choice</Pill>;
  const tip = gateway?.tenantTipConfig || null;
  const pcts: number[] = Array.isArray(tip?.percentages) ? tip.percentages : [];
  const ts = gateway?.tenantSurchargeConfig || {};
  const agreement = activeAgreementOf(gateway);
  const maxRate = Number(gateway?.maxSurchargeRate ?? agreement?.maxRate ?? 0);
  const maxFlat = Number(gateway?.maxSurchargeFlat ?? agreement?.maxFlat ?? 0);
  const min = Number(gateway?.minCardAmount ?? 0);
  const chip = (txt: string, on = true) => (
    <span key={txt} className={`inline-flex h-9 items-center rounded-full border px-3 text-[13px] ${on ? 'border-ruby bg-[#FFF1F3] text-ruby-deep' : 'border-stone-200 bg-white text-stone-500'}`}>{txt}</span>
  );
  return (
    <Card
      title="Tip choices, surcharge rate & card minimum"
      sub={<>Set by the merchant for <b>every till at {locationName}</b>. Shown here read-only.</>}
      badge={<ScopeBadge kind="loc">Every till at location</ScopeBadge>}
    >
      <Row testId="pt-loc-tips" icon={<DollarSign size={18} />} title={<>Tip choices {tag}</>}
        desc={gateway ? 'One list, used by Cash, Credit and Debit wherever tips are on' : 'Could not read the location settings.'}>
        {gateway && (
          <div className="mt-2 flex flex-wrap gap-1.5">
            {pcts.length ? pcts.map((p) => chip(`${p}%`)) : chip('No percentages set', false)}
            {chip('Custom amount', tip?.allowCustom !== false)}
            {chip('No tip', tip?.allowSkip !== false)}
          </div>
        )}
      </Row>
      {approved && (
        <Row testId="pt-loc-rate" icon={<Percent size={18} />} title={<>Surcharge rate {tag}</>}
          desc={`Max allowed: ${maxRate.toFixed(2)}% + $${maxFlat.toFixed(2)}${agreement ? ` · ${agreement.agreementName || agreement.fileName || 'active agreement'}` : ''}.`}
          right={<span className="text-sm font-medium tabular-nums text-stone-800">
            {Number(ts.rateOverride) > 0 ? `${Number(ts.rateOverride).toFixed(2)}%` : `${maxRate.toFixed(2)}% (max)`}
            {' + '}${(Number(ts.flatOverride) > 0 ? Number(ts.flatOverride) : maxFlat).toFixed(2)}
          </span>}
        />
      )}
      <Row testId="pt-loc-min" icon={<MinusSquare size={18} />} title={<>Minimum card amount {tag}</>}
        desc={min > 0 ? `Under $${min.toFixed(2)}, the Card tile is disabled and says why. Cash is unaffected.` : 'No minimum — $0.'}
        right={<span className="text-sm font-medium tabular-nums text-stone-800">${min.toFixed(2)}</span>}
      />
    </Card>
  );
};

export default RegisterPaymentTypes;
